import { Vue, Ref, Prop } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { BModal } from 'bootstrap-vue/esm/components/modal';
import { Photo, PublicPhoto } from '@/models/photo';
import { photoService } from '@/services/';
import { Storage } from 'aws-amplify';
import { PinturaEditor, PinturaEditorModal, PinturaEditorOverlay } from '@pqina/vue-pintura/vue-2';
import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,
  locale_en_gb,

  // plugins
  setPlugins,
  plugin_crop,
  plugin_crop_locale_en_gb,
  plugin_filter,
  plugin_filter_defaults,
  plugin_filter_locale_en_gb,
  plugin_finetune,
  plugin_finetune_defaults,
  plugin_finetune_locale_en_gb,
  plugin_annotate,
  plugin_annotate_locale_en_gb,
  markup_editor_defaults,
  markup_editor_locale_en_gb,
  plugin_frame,
  plugin_frame_locale_en_gb,
  plugin_frame_defaults,
  plugin_resize,
  plugin_resize_locale_en_gb,
} from '@pqina/pintura';

import PremiumOverlay from '../premiumoverlay/premiumoverlay.component.vue';
import { Logger } from 'aws-amplify';

import fr_FR from '@pqina/pintura/locale/fr_FR/index.js';
import en_GB from '@pqina/pintura/locale/en_GB/index.js';

setPlugins(plugin_crop, plugin_finetune, plugin_filter, plugin_annotate, plugin_frame, plugin_resize);

const logger = new Logger('EditShareModal');

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as string); // Résultat en base64
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob); // Lire le Blob en tant que Data URL
  });
}

@Component({
  components: {
    PinturaEditor,
    PinturaEditorModal,
    PinturaEditorOverlay,
    PremiumOverlay,
  },
})
export default class EditShareModal extends Vue {
  @Prop({ type: Object, required: true }) private readonly image!: Photo;
  @Prop({ required: true }) readonly isPremium!: boolean;

  @Ref('edit-share-modal') private modal!: BModal;

  private locale = 'fr';
  private customLocale = {
    ...fr_FR,
    statusImageLoadPrepare: "Chargement de l'image&hellip;",
    // Vous pouvez ajouter d'autres surcharges ici
  };
  private editorProps = {
    imageReader: createDefaultImageReader(),
    imageWriter: createDefaultImageWriter(),
    shapePreprocessor: createDefaultShapePreprocessor(),
    ...plugin_finetune_defaults,
    ...plugin_filter_defaults,
    ...markup_editor_defaults,
    ...plugin_frame_defaults,
    locale: this.customLocale,
    enableButtonExport: false,
    cropEnableButtonFlipVertical: true,
    cropEnableButtonRotateRight: true,
  };
  private inlineSrc = 'https://images.photowall.com/products/42556/summer-landscape-with-river.jpg'; // Will be populated with the S3 image link
  private inlineResult = '';
  private photoUploading = false;
  private imageCropAspectRatio: number | null = null;
  private processedImage: Blob | null = null;

  public show(): void {
    Storage.get(this.image.key, { expires: 3600 })
      .then((link: string | unknown) => {
        if (typeof link === 'string') {
          this.inlineSrc = link; // Passez l'URL à Pintura pour charger l'image
        } else {
          console.error('Expected a string but got an unknown type:', link);
        }
        this.modal.show(); // Affiche le modal
      })
      .catch((error) => {
        console.error('Error fetching image from S3:', error);
      });
  }

  public onShown(): void {
    this.$nextTick(() => {
      console.log('Editor initialized with:', this.inlineSrc);
    });
  }

  public handleInlineLoad(event: any): void {
    if (event && event.src) {
      console.log('Image loaded successfully:', event.src);
    } else {
      console.error('Image load failed:', event);
    }
  }

  public handleInlineProcess(event: any): void {
    if (event.dest) {
      this.inlineResult = URL.createObjectURL(event.dest); // Générer un URL pour l'image traitée
      this.processedImage = event.dest;
      console.log('Processed image result:', this.inlineResult);
    } else {
      console.error('Processing event did not contain a destination image:', event);
    }
  }

  public close(): void {
    this.modal.hide();
    logger.info('close');
  }

  public async saveImageBeforeSharing(base64Image: string): Promise<string> {
    this.photoUploading = true;

    // Requête à l'API pour obtenir l'URL signée
    return photoService.uploadModifiedVersion(this.image.id).then(async (result: PublicPhoto) => {
      if (result.putUrl) {
        const options = {
          headers: {
            'Content-Type': 'image/jpeg',
            'Content-Encoding': 'base64',
          },
        };
        // Envoi du buffer de l'image en base64 à l'URL signée
        const buffer = Buffer.from(base64Image.replace(/^data:image\/\w+;base64,/, ''), 'base64');
        await this.axios.put(result.putUrl, buffer, options);
        this.photoUploading = false;
        return result.id;
      } else {
        throw new Error('No signed URL returned by the backend');
      }
    });
  }

  public buildShareLink(key: string): string {
    return `${process.env.VUE_APP_API_ENDPOINT}/photos/${key}/viewer`;
  }

  public shareFacebook(): void {
    const editor = (this.$refs.pinturaEditor as any)?.editor;
    if (editor) {
      editor
        .processImage() // Traite l'image
        .then(({ dest }: any) => {
          if (dest) {
            // Convertir en base64 pour Facebook
            blobToBase64(dest).then((base64Image: string) => {
              this.saveImageBeforeSharing(base64Image).then((id) => {
                window.open(
                  'https://www.facebook.com/sharer/sharer.php?u=' + this.buildShareLink(id),
                  '',
                  'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600'
                );
              });
            });
          }
        })
        .catch((error: any) => {
          console.error('Error processing image for Facebook sharing:', error);
        });
    }
  }

  public shareTwitter(): void {
    const editor = (this.$refs.pinturaEditor as any)?.editor;
    if (editor) {
      editor
        .processImage() // Traite l'image
        .then(({ dest }: any) => {
          if (dest) {
            // Convertir le Blob en base64 pour l'upload
            blobToBase64(dest).then((base64Image: string) => {
              this.saveImageBeforeSharing(base64Image).then((id) => {
                window.open(
                  'https://twitter.com/intent/tweet?text=Check out this modified image!&url=' + this.buildShareLink(id),
                  '',
                  'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600'
                );
              });
            });
          }
        })
        .catch((error: any) => {
          console.error('Error processing image for Twitter sharing:', error);
        });
    }
  }

  public shareLinkedin(): void {
    const editor = (this.$refs.pinturaEditor as any)?.editor;
    if (editor) {
      editor
        .processImage() // Traite l'image
        .then(({ dest }: any) => {
          if (dest) {
            // Convertir le Blob en base64
            blobToBase64(dest).then((base64Image: string) => {
              this.saveImageBeforeSharing(base64Image).then((id) => {
                window.open(
                  'https://www.linkedin.com/sharing/share-offsite/?url=' + this.buildShareLink(id),
                  '',
                  'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600'
                );
              });
            });
          }
        })
        .catch((error: any) => {
          console.error('Error processing image for LinkedIn sharing:', error);
        });
    }
  }

  public shareEmail(): void {
    const editor = (this.$refs.pinturaEditor as any)?.editor;
    if (editor) {
      editor
        .processImage() // Traite l'image
        .then(({ dest }: any) => {
          if (dest) {
            // Convertir le Blob en base64
            blobToBase64(dest).then((base64Image: string) => {
              this.saveImageBeforeSharing(base64Image).then((id) => {
                // Obtenir l'URL de l'image après l'avoir sauvegardée
                const imageUrl = this.buildShareLink(id);
                const mailtoLink = `mailto:?subject=Photo%20du%20chantier&body=Voici une image modifiée du chantier : ${imageUrl}`;
                window.open(mailtoLink);
              });
            });
          }
        })
        .catch((error: any) => {
          console.error('Error processing image for email sharing:', error);
        });
    }
  }

  public downloadImage(): void {
    const editor = (this.$refs.pinturaEditor as any)?.editor; // Assurez-vous d'accéder à l'instance de l'éditeur
    if (editor) {
      editor
        .processImage() // Traite l'image immédiatement
        .then(({ dest }: any) => {
          if (dest) {
            // Crée un lien de téléchargement
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(dest); // Utiliser l'image traitée
            downloadLink.download = 'modified-image.jpg'; // Nom du fichier à télécharger
            document.body.appendChild(downloadLink);
            downloadLink.click(); // Déclenche le téléchargement
            document.body.removeChild(downloadLink); // Supprime le lien après le téléchargement
          }
        })
        .catch((error: any) => {
          console.error('Error processing image:', error);
        });
    } else {
      console.error('Editor is not available.');
    }
  }
}
